/**
 * Terrain surface material (issue #61). Two paths, one shared material per tier:
 *
 *  - high tier: a MeshStandardMaterial extended via onBeforeCompile with a
 *    continuous slope/altitude splat blend broken up by baked multi-scale noise
 *    (macro tint / meso albedo band / micro near-camera bump), so the ground
 *    reads as real surface instead of a hard-banded, shaded heightmap.
 *  - low tier: the plain vertex-colour material (computeTintColors below) with
 *    no shader injection and zero extra texture fetches — the measured-cost
 *    fallback for the low-power target (issue #17).
 *
 * The blend cascade and macro/meso/micro breakup-layer design are adapted from
 * Braffolk/fable5-world-demo `TerrainMaterial.ts` (MIT, commit fd75fdb) — see
 * outputs/qa/mining/05_visual_quality.md §3. The reference is WebGPU/TSL; this
 * re-expresses the same maths for WebGL2 via a CPU noise bake + GLSL injection.
 *
 * Purely cosmetic — never read back for any LOS or elevation calculation (those
 * go through terrain/collider.ts or, for LOS truth, raypath/'s analytical
 * outputs). This module never imports from raypath/.
 */
import {
  BufferGeometry,
  Color,
  DataTexture,
  DoubleSide,
  Float32BufferAttribute,
  LinearFilter,
  LinearMipmapLinearFilter,
  MeshStandardMaterial,
  MirroredRepeatWrapping,
  RGBAFormat,
} from 'three';
import type { QualityTier } from '../core/quality';
import type { ShadingPreset } from '../core/shadingPreset';
import {
  ALTITUDE_EDGES,
  SLOPE_EDGES,
  TERRAIN_CLASS_COLORS,
  TERRAIN_NOISE_SEED,
  TERRAIN_PALETTE_HEX,
  generateTerrainNoiseData,
  srgbToLinearRgb,
  terrainAlbedo,
  type RGB,
} from './terrainNoise';

/**
 * Elevation tint bands, metres AOD. Tuned to the Ray II AOI's observed range
 * (~150-340 m AOD). The band COLOURS are derived from the single
 * TERRAIN_PALETTE_HEX source (issue #90) so this low-tier vertex-colour path
 * can never drift in hue from the high-tier splat path, which consumes the
 * same map via TERRAIN_CLASS_COLORS. Only the altitude cut-offs live here; the
 * hues are owned by terrainNoise.ts. (`terrainClassColorsMatchBands` in the
 * unit tests asserts these Colors equal the corresponding class colours.)
 */
const ELEVATION_BANDS: Array<{ upTo: number; color: Color }> = [
  { upTo: 180, color: new Color(TERRAIN_PALETTE_HEX.grass) }, // valley / lower pasture
  { upTo: 230, color: new Color(TERRAIN_PALETTE_HEX.moor) }, // upland moor
  { upTo: 280, color: new Color(TERRAIN_PALETTE_HEX.heather) }, // high moor
  { upTo: 330, color: new Color(TERRAIN_PALETTE_HEX.ridge) }, // ridge
  { upTo: Infinity, color: new Color(TERRAIN_PALETTE_HEX.summit) }, // summit
];

/** The palette hex each elevation band draws from, in band order — exported so
 * a unit test can assert the two tiers use identical hex values (issue #90). */
export const ELEVATION_BAND_HEX: readonly number[] = [
  TERRAIN_PALETTE_HEX.grass,
  TERRAIN_PALETTE_HEX.moor,
  TERRAIN_PALETTE_HEX.heather,
  TERRAIN_PALETTE_HEX.ridge,
  TERRAIN_PALETTE_HEX.summit,
];

/** Steep faces darken by up to this fraction, for visual slope legibility. */
const SLOPE_DARKEN_MAX = 0.35;

function elevationColor(y: number): Color {
  for (const band of ELEVATION_BANDS) {
    if (y <= band.upTo) return band.color;
  }
  return ELEVATION_BANDS[ELEVATION_BANDS.length - 1].color;
}

/**
 * Per-vertex RGB colour array for a terrain tile's geometry. Pure function
 * over geometry attributes — no renderer required, so it is directly
 * unit-testable in Node. This is the low-tier fallback palette and the base
 * colour the high-tier shader builds on.
 *
 * Two shading presets (issue #106), selected per vertex from the same
 * altitude/slope inputs:
 *  - `stylised` (default): the original hard elevation-band + slope-darkening
 *    look, byte-identical to the pre-#106 output.
 *  - `natural`: a continuous ramp via the shared `terrainAlbedo(altitude,
 *    slope)` (terrainNoise.ts) — the same source of truth the high-tier splat
 *    shader draws on (issue #90 "no hue drift" doctrine) — converted from its
 *    sRGB authoring space to the renderer's linear working space, matching
 *    what `new Color(hex)` does implicitly for the stylised band colours.
 *    `SLOPE_DARKEN_MAX` is a stylised-only embellishment: terrainAlbedo
 *    already encodes slope via its scree/rock classes, so natural does not
 *    apply it on top.
 */
export function computeTintColors(geometry: BufferGeometry, preset: ShadingPreset = 'stylised'): Float32Array {
  const position = geometry.getAttribute('position');
  if (!position) throw new Error('geometry has no position attribute');
  if (!geometry.getAttribute('normal')) geometry.computeVertexNormals();
  const normal = geometry.getAttribute('normal');

  const colors = new Float32Array(position.count * 3);
  const c = new Color();
  for (let i = 0; i < position.count; i++) {
    const y = position.getY(i);
    // |y|, not y: the pipeline's TIN export winding is not guaranteed (see the
    // DoubleSide note on createTerrainMaterial), so normals can point down on
    // flat ground and the sign carries no slope information. Shared by both
    // presets so the winding canonicalisation can never drift between them.
    const ny = Math.abs(normal.getY(i));
    const slope = 1 - Math.min(1, ny); // 0 = flat, 1 = vertical face
    if (preset === 'natural') {
      const [r, g, b] = srgbToLinearRgb(terrainAlbedo(y, slope));
      colors[i * 3] = r;
      colors[i * 3 + 1] = g;
      colors[i * 3 + 2] = b;
    } else {
      c.copy(elevationColor(y)).multiplyScalar(1 - slope * SLOPE_DARKEN_MAX);
      colors[i * 3] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
    }
  }
  return colors;
}

/** Computes and attaches the vertex-colour attribute in place. */
export function applyTerrainTint(geometry: BufferGeometry, preset: ShadingPreset = 'stylised'): void {
  geometry.setAttribute('color', new Float32BufferAttribute(computeTintColors(geometry, preset), 3));
}

// ---------------------------------------------------------------------------
// Splat-shading (high tier)
// ---------------------------------------------------------------------------

/** Size of the baked noise texture. 256² of RGBA is a 256 KB one-off upload. */
const NOISE_TEXTURE_SIZE = 256;

/** World-space sampling periods (metres) for each breakup layer. Two decorrelated
 * macro periods hide the texture's own mirror-repeat far beyond route-viewing
 * distances; meso/micro add close-range detail. */
const MACRO_PERIOD_A = 63;
const MACRO_PERIOD_B = 170;
const MESO_PERIOD = 3;
const MICRO_PERIOD = 0.5;

/** Baked once, shared by every high-tier material and therefore every tile.
 * Lazily created so pure-logic tests never build a texture they don't need. */
let sharedNoiseTexture: DataTexture | null = null;

function getSharedNoiseTexture(): DataTexture {
  if (sharedNoiseTexture) return sharedNoiseTexture;
  const data = generateTerrainNoiseData(NOISE_TEXTURE_SIZE, TERRAIN_NOISE_SEED);
  const tex = new DataTexture(data, NOISE_TEXTURE_SIZE, NOISE_TEXTURE_SIZE, RGBAFormat);
  tex.wrapS = MirroredRepeatWrapping;
  tex.wrapT = MirroredRepeatWrapping;
  tex.magFilter = LinearFilter;
  tex.minFilter = LinearMipmapLinearFilter;
  tex.generateMipmaps = true; // mip-filtering tames grazing-angle shimmer on the micro layer
  tex.needsUpdate = true;
  sharedNoiseTexture = tex;
  return tex;
}

function glslVec3(rgb: RGB): string {
  return `vec3(${rgb[0].toFixed(5)}, ${rgb[1].toFixed(5)}, ${rgb[2].toFixed(5)})`;
}

function num(n: number): string {
  return Number.isInteger(n) ? `${n}.0` : `${n}`;
}

/** GLSL constants emitted from the TS palette/edges so the shader and the pure
 * terrainAlbedo() unit tests share one source of truth for the class-weight
 * boundaries. Palette colours are emitted linear (srgbToLinearRgb) because the
 * shader assigns them straight into the renderer's linear-space diffuseColor. */
function terrainClassGlsl(): string {
  const C = TERRAIN_CLASS_COLORS;
  const A = ALTITUDE_EDGES;
  const S = SLOPE_EDGES;
  const lin = (rgb: RGB): string => glslVec3(srgbToLinearRgb(rgb));
  return /* glsl */ `
vec3 terrainClassAlbedo(float alt, float slope) {
  vec3 col = ${lin(C.grass)};
  col = mix(col, ${lin(C.moor)},    smoothstep(${num(A.moorLo)}, ${num(A.moorHi)}, alt));
  col = mix(col, ${lin(C.heather)}, smoothstep(${num(A.heatherLo)}, ${num(A.heatherHi)}, alt));
  col = mix(col, ${lin(C.ridge)},   smoothstep(${num(A.ridgeLo)}, ${num(A.ridgeHi)}, alt));
  col = mix(col, ${lin(C.summit)},  smoothstep(${num(A.summitLo)}, ${num(A.summitHi)}, alt));
  float rockW  = smoothstep(${num(S.rockLo)}, ${num(S.rockHi)}, slope);
  float screeW = smoothstep(${num(S.screeLo)}, ${num(S.screeHi)}, slope) * (1.0 - rockW);
  col = mix(col, ${lin(C.scree)}, screeW);
  col = mix(col, ${lin(C.rock)},  rockW);
  return col;
}
`;
}

const VERT_HEADER = /* glsl */ `
varying vec3 vTerrainWorldPos;
varying float vTerrainSlope;
`;

const VERT_BODY = /* glsl */ `
  vec4 tWorld = modelMatrix * vec4(transformed, 1.0);
  vTerrainWorldPos = tWorld.xyz;
  vec3 tWorldNormal = normalize(mat3(modelMatrix) * objectNormal);
  // abs(): TIN winding is not guaranteed, so flat ground can carry a
  // downward normal; without abs() it reads as a vertical face and the whole
  // landscape classifies as rock.
  vTerrainSlope = 1.0 - clamp(abs(tWorldNormal.y), 0.0, 1.0);
`;

const FRAG_HEADER = /* glsl */ `
uniform sampler2D tTerrainNoise;
varying vec3 vTerrainWorldPos;
varying float vTerrainSlope;
${terrainClassGlsl()}
`;

/** Fragment override: continuous, noise-dithered splat albedo. Exactly four
 * texture fetches (macroA, macroB, meso, micro) — within the ≤4 high-tier
 * budget. Runs after <color_fragment> so vColor is still the low-tier base. */
const FRAG_BODY = /* glsl */ `
  {
    vec2 wxz = vTerrainWorldPos.xz;
    float alt = vTerrainWorldPos.y;
    float macroA = texture2D(tTerrainNoise, wxz / ${num(MACRO_PERIOD_A)}).r;
    float macroB = texture2D(tTerrainNoise, wxz / ${num(MACRO_PERIOD_B)} + 0.37).r;
    float meso   = texture2D(tTerrainNoise, wxz / ${num(MESO_PERIOD)}).g;
    float micro  = texture2D(tTerrainNoise, wxz / ${num(MICRO_PERIOD)}).b;
    float macro = mix(macroA, macroB, 0.5);
    // dither the class edges so bands never read as contour lines
    float altD = alt + (macro - 0.5) * 16.0;
    float slopeD = clamp(vTerrainSlope + (meso - 0.5) * 0.04, 0.0, 1.0);
    vec3 albedo = terrainClassAlbedo(altD, slopeD);
    // macro ±8% tint variation (2-50 m features)
    albedo *= 1.0 + (macro - 0.5) * 0.16;
    // meso albedo band
    albedo *= 1.0 + (meso - 0.5) * 0.10;
    // micro near-camera bump: subtle crevice darkening, distance-gated so it
    // vanishes past ~150 m and never fights the flat-shaded-TIN read at range
    float camDist = distance(cameraPosition, vTerrainWorldPos);
    float nearW = 1.0 - smoothstep(60.0, 150.0, camDist);
    albedo *= 1.0 - (micro - 0.5) * 0.12 * nearW;
    diffuseColor.rgb = albedo;
  }
`;

function applySplatShader(material: MeshStandardMaterial): void {
  const noise = getSharedNoiseTexture();
  material.userData.terrainSplat = true;
  material.userData.terrainNoiseTexture = noise;
  material.onBeforeCompile = (shader) => {
    shader.uniforms.tTerrainNoise = { value: noise };
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\n${VERT_HEADER}`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>\n${VERT_BODY}`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${FRAG_HEADER}`)
      .replace('#include <color_fragment>', `#include <color_fragment>\n${FRAG_BODY}`);
  };
}

/**
 * Shared terrain material for the given tier and shading preset (issue #106).
 * The caller builds this once and assigns the single instance to every tile
 * (no per-tile materials — issue #17). `side: DoubleSide` is a
 * raycast-continuity requirement (AC-N2), not a visual choice: the pipeline's
 * TIN export winding order is not guaranteed, and a FrontSide-only material
 * silently drops downward raycasts on any triangle wound the "wrong" way,
 * breaking collider continuity and walk-mode ground lock.
 *
 * `flatShading` is the only material-level effect of the preset: `stylised`
 * facets every triangle (today's look), `natural` shades from the geometry's
 * (already-computed, see computeTintColors) smooth vertex normals. The tier
 * axis is untouched by the preset — a high-tier splat material still gets
 * `applySplatShader` under `natural`, and a low-tier material still gets none
 * under `stylised`; every tier x preset combination is valid.
 */
export function createTerrainMaterial(
  tier: QualityTier = 'high',
  preset: ShadingPreset = 'stylised',
): MeshStandardMaterial {
  const material = new MeshStandardMaterial({
    vertexColors: true,
    flatShading: preset === 'stylised',
    roughness: 0.95,
    metalness: 0.0,
    side: DoubleSide,
  });
  if (tier === 'high') applySplatShader(material);
  return material;
}

/**
 * Applies a shading-preset switch to an already-live material (issue #106):
 * flips `flatShading` and marks the material for GLSL recompilation.
 * `MeshStandardMaterial#flatShading` is read once at shader compile time, so
 * a live toggle needs `needsUpdate = true` or the renderer keeps the old
 * shading mode. A no-op (no `needsUpdate` bump) when the preset is unchanged,
 * since this is also called on tile attach where the preset frequently hasn't
 * changed since the previous attach.
 */
export function applyShadingPresetToMaterial(material: MeshStandardMaterial, preset: ShadingPreset): void {
  const flat = preset === 'stylised';
  if (material.flatShading === flat) return;
  material.flatShading = flat;
  material.needsUpdate = true;
}
